import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import BonanzaPopup from "@/pages/user/BonanzaPopup";
import {
  LayoutDashboard, User, Wallet, TrendingUp, ArrowDownToLine, Network, Users,
  UsersRound, Trophy, Banknote, Gem, Receipt, Bell, ShieldCheck, LogOut, Menu,
  Volume2, VolumeX, Sun, Moon, LifeBuoy, ChevronDown, Megaphone, Gift, CandlestickChart,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import useDashTheme from "@/lib/useDashTheme";
import NotificationBell from "@/components/NotificationBell";
import RankCelebration from "@/components/RankCelebration";
import { rankMeta } from "@/lib/ranks";

const TOP = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/profile", label: "Profile", icon: User },
  { to: "/app/pink-pin", label: "Pink Pin", icon: Gift, pinkPinOnly: true },
  { label: "Self Trade", icon: CandlestickChart, items: [
    { to: "/app/trading", label: "Trading Terminal", icon: CandlestickChart },
    { to: "/app/self-trade/commission", label: "Self Trade IB", icon: Network },
  ]},
];

const GROUPS = [
  { label: "My Network", icon: Network, items: [
    { to: "/app/directs", label: "My Directs", icon: Users },
    { to: "/app/binary", label: "Binary Tree", icon: Network },
    { to: "/app/team", label: "My Team", icon: UsersRound },
  ] },
  { label: "Income", icon: TrendingUp, items: [
    { to: "/app/income/direct", label: "Referral Bonus", icon: Users },
    { to: "/app/income/binary", label: "Performance IB", icon: Network },
    { to: "/app/income/roi", label: "ROI Income", icon: TrendingUp },
    { to: "/app/rewards", label: "Performance Reward", icon: Trophy },
    { to: "/app/salary", label: "Activity IB Salary", icon: Banknote },
  ] },
  { label: "Deposit", icon: Wallet, items: [
    { to: "/app/deposit", label: "Deposit", icon: Wallet, end: true },
    { to: "/app/deposit/history", label: "Deposit History", icon: Receipt },
  ] },
  { label: "Withdrawal", icon: ArrowDownToLine, items: [
    { to: "/app/withdrawal", label: "Withdrawal", icon: ArrowDownToLine, end: true },
    { to: "/app/withdrawal/history", label: "Withdrawal History", icon: Receipt },
  ] },
  { label: "Support", icon: LifeBuoy, items: [
    { to: "/app/support", label: "Help & Tickets", icon: LifeBuoy },
  ] },
];

const BOTTOM = [
  { to: "/app/ranks", label: "Ranks", icon: Gem },
  { to: "/app/transactions", label: "Transactions", icon: Receipt },
  { to: "/app/notifications", label: "Notifications", icon: Bell },
  { to: "/app/security", label: "Security", icon: ShieldCheck },
];

const tid = (s) => s.toLowerCase().replace(/[ &]+/g, "-");

function NavItem({ l, unread }) {
  return (
    <NavLink to={l.to} end={l.end}
      className={({ isActive }) => cn("sidebar-link", isActive && "active")}
      data-testid={`nav-${tid(l.label)}`}>
      <l.icon size={18} />
      <span className="flex-1">{l.label}</span>
      {l.label === "Notifications" && unread > 0 && (
        <span className="rounded-full bg-[var(--gold)] px-1.5 text-[10px] font-bold text-black">{unread}</span>
      )}
    </NavLink>
  );
}

function NavGroup({ group, pathname }) {
  const hasActive = group.items.some((i) => pathname === i.to || pathname.startsWith(i.to + "/"));
  const [open, setOpen] = useState(hasActive);
  useEffect(() => { if (hasActive) setOpen(true); }, [hasActive]);
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} className="sidebar-link w-full" data-testid={`navgroup-${tid(group.label)}`}>
        <group.icon size={18} />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown size={16} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="ml-3 mt-1 flex flex-col gap-1 border-l border-white/10 pl-3">
          {group.items.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              className={({ isActive }) => cn("sidebar-link text-sm", isActive && "active")}
              data-testid={`nav-${tid(l.label)}`}>
              <l.icon size={16} />
              <span className="flex-1">{l.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UserLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useState(localStorage.getItem("sound") === "on");
  const [unread, setUnread] = useState(0);
  const [news, setNews] = useState([]);
  const [dark, toggleTheme] = useDashTheme();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    api.get("/user/news").then((r) => setNews(r.data.news || [])).catch(() => {});
  }, [loc.pathname]);

  const toggleSound = () => {
    const next = !sound; setSound(next);
    localStorage.setItem("sound", next ? "on" : "off");
  };

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="grain min-h-screen">
      {/* Sidebar */}
      <aside className={cn(
        "sidebar-crypto fixed inset-y-0 left-0 z-40 w-72 transition-transform lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex h-16 items-center gap-2 border-b border-white/10 px-4">
          <img src="/logo.png" alt="Alexa Fintech" className="h-12 w-auto object-contain" />
        </div>
        <nav className="flex h-[calc(100vh-4rem)] flex-col gap-1 overflow-y-auto p-4">
          {TOP.map((l) => {
            if (l.pinkPinOnly && user?.status === "active" && user?.activation_type !== "PINK_PIN") return null;
            return l.items
              ? <NavGroup key={l.label} group={l} pathname={loc.pathname} />
              : <NavItem key={l.to} l={l} />;
          })}
          <div className="my-2 border-t border-white/10" />
          {GROUPS.map((g) => <NavGroup key={g.label} group={g} pathname={loc.pathname} />)}
          <div className="my-2 border-t border-white/10" />
          {BOTTOM.map((l) => <NavItem key={l.to} l={l} unread={unread} />)}
          <button onClick={doLogout} className="sidebar-link mt-2 text-rose-300 hover:bg-rose-500/10" data-testid="nav-logout">
            <LogOut size={18} /> Logout
          </button>
        </nav>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className={cn("lg:pl-72 min-h-screen theme-app theme-dark bg-[var(--bg)]")}>
        <ImpersonationBanner />
        <RankCelebration rank={user?.rank} />
        {news.length > 0 && (
          <div className="flex items-center gap-2 border-b border-black/10 bg-[var(--gold)] px-4 py-1.5 text-sm font-semibold text-[#3d1f00]" data-testid="news-ticker">
            <Megaphone size={16} className="shrink-0" />
            <div className="marquee-wrap"><span className="marquee-track">{(() => { const s = news.map((n) => n.message).join("      •      "); return s + "      •      " + s; })()}</span></div>
          </div>
        )}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-white/10 bg-[var(--bg)]/80 px-4 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-white" onClick={() => setOpen(true)} data-testid="sidebar-toggle"><Menu /></button>
            <div className="hidden sm:block">
              <p className="text-xs text-[var(--text-dim)]">Member ID</p>
              <p className="text-sm font-semibold text-white" data-testid="topbar-member-id">{user?.member_id}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {(() => { const rm = rankMeta(user?.rank); const RI = rm.Icon; return (
              <span className="hidden items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold sm:inline-flex" style={{ background: "#0a0f0c", borderColor: rm.color + "88", color: rm.color }} data-testid="topbar-rank">
                <RI size={14} /> {user?.rank || "Associate"}
              </span>
            ); })()}
            <span className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold capitalize",
              user?.status === "active" ? "border-emerald-400/40 text-emerald-300" : "border-amber-400/40 text-amber-300")}
              style={{ background: "#1a0a10" }}
              data-testid="topbar-status">{user?.status}</span>
            <button onClick={toggleSound} className="grid h-9 w-9 place-items-center rounded-xl glass-2 text-white" title="Sound" data-testid="sound-toggle">
              {sound ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <NotificationBell unread={unread} setUnread={setUnread} sound={sound} />
            <button onClick={toggleTheme} className="grid h-9 w-9 place-items-center rounded-xl glass-2 text-white" title="Toggle theme" data-testid="theme-toggle">
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="grid h-9 w-9 place-items-center rounded-xl btn3d-gold text-sm font-bold text-black">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <button onClick={doLogout} className="flex items-center gap-1.5 rounded-xl glass-2 px-3 py-2 text-xs font-semibold text-rose-500" data-testid="header-logout">
              <LogOut size={15} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>
        <main className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8"><Outlet /></main>
        <BonanzaPopup />
      </div>
    </div>
  );
}

function ImpersonationBanner() {
  const adminToken = localStorage.getItem("admin_token");
  if (!adminToken) return null;
  const exit = () => {
    localStorage.setItem("token", adminToken);
    localStorage.removeItem("admin_token");
    window.location.href = "/admin/users";
  };
  return (
    <div className="flex items-center justify-center gap-3 px-4 py-2 text-center text-sm" style={{ background: "linear-gradient(90deg, #7A1830, #541024)", color: "#fff" }} data-testid="impersonation-banner">
      <span>You are viewing a member panel as an administrator.</span>
      <button onClick={exit} className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold hover:bg-white/30" style={{ color: "#fff" }} data-testid="exit-impersonation">Exit to Admin</button>
    </div>
  );
}
